import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../common/Button';
import Box from 'apollo-react/components/Box';
import Typography from '../../common/StyledTypography';
import StyledTabs from '../../common/StyledTabs/StyledTabs';
import StarTab from '../../common/StyledTabs/StarTab';
import SubformPreview from '../../common/SubformPreview/SubformPreview';
import { convertToFormCrafterSchema } from '../../../utils/formCrafter/schemaUtils';
import {
  FormBuilder,
  createEmptySchema,
} from '@iqvia-technology-solutions/formcrafter';
import '@iqvia-technology-solutions/formcrafter/dist/esm/styles/formcrafter.css';
import '../../common/FormCrafterFormRenderer/form_builder.css';

const FormBuilderComponent = ({
  formData,
  onFormSave,
  isTemplate = false,
  handleDiscardModal,
  saveRef,
  onSchemaChange,
}) => {
  const [t] = useTranslation();
  const [activeTab, setActiveTab] = useState('builder');
  const currentSchemaRef = useRef(null);
  const originalSchemaRef = useRef(null);
  const [initialSchema, setInitialSchema] = useState(() => {
    if (formData) {
      try {
        const parsedData =
          typeof formData === 'string' ? JSON.parse(formData) : formData;
        return parsedData;
      } catch (error) {
        console.error('Error parsing formData:', error);
        return createEmptySchema();
      }
    }
    return createEmptySchema();
  });

  useEffect(() => {
    if (formData) {
      try {
        const parsedData =
          typeof formData === 'string' ? JSON.parse(formData) : formData;
        setInitialSchema(parsedData);
        currentSchemaRef.current = parsedData;
        originalSchemaRef.current = parsedData;
      } catch (error) {
        console.error('Error parsing formData:', error);
      }
    }
  }, [formData, isTemplate]);

  const handleSaveSchema = (schema) => {
    currentSchemaRef.current = schema;
    if (onSchemaChange) {
      const dirty =
        JSON.stringify(schema) !== JSON.stringify(originalSchemaRef.current);
      onSchemaChange(dirty);
    }
  };

  const saveForm = async () => {
    const formJsonData = JSON.stringify(
      currentSchemaRef.current || initialSchema
    );
    const result = await onFormSave(formJsonData);
    if (result !== false) {
      originalSchemaRef.current = currentSchemaRef.current;
      if (onSchemaChange) onSchemaChange(false);
    }
    return result;
  };

  if (saveRef) saveRef.current = saveForm;

  // Convert current schema to IDS-compatible format for preview
  const previewSchema = useMemo(() => {
    const raw = currentSchemaRef.current || initialSchema;
    if (!raw || !raw.fields || raw.fields.length === 0) return null;
    return convertToFormCrafterSchema(raw);
  }, [initialSchema]);

  const handleTabChange = (event, value) => {
    const newTab = value === 0 ? 'builder' : 'preview';

    // When switching away from builder, save the current state to initialSchema
    if (activeTab === 'builder' && newTab === 'preview') {
      if (currentSchemaRef.current) {
        setInitialSchema(currentSchemaRef.current);
      }
    }

    setActiveTab(newTab);
  };

  return (
    <Box className="form-builder-container">
      {/* Tab Navigation */}
      <StyledTabs
        value={activeTab === 'builder' ? 0 : 1}
        onChange={handleTabChange}
        className="form-builder-tabs"
        truncate
      >
        <StarTab
          value={0}
          label={t('Builder')}
          isActive={activeTab === 'builder'}
        />
        <StarTab
          value={1}
          label={t('Preview')}
          isActive={activeTab === 'preview'}
        />
      </StyledTabs>

      {/* Tab Content */}
      <Box className="form-builder-tab-content">
        {activeTab === 'builder' && (
          <FormBuilder
            onSave={handleSaveSchema}
            initialSchema={initialSchema}
          />
        )}

        {activeTab === 'preview' && (
          <Box className="form-builder-preview-container">
            {previewSchema ? (
              <>
                <Box className="form-builder-preview-header">
                  <Typography
                    variant="h6"
                    className="form-builder-preview-title"
                  >
                    {t('Form Preview')}
                  </Typography>
                  <Typography
                    variant="bodySmall"
                    className="form-builder-preview-description"
                  >
                    {t('form_preview_description')}
                  </Typography>
                </Box>
                <SubformPreview schema={previewSchema} disabled={false} />
              </>
            ) : (
              <Box className="form-builder-empty-state">
                <Typography variant="h6" className="form-builder-empty-title">
                  {t('no_form_fields')}
                </Typography>
                <Typography
                  variant="bodySmall"
                  className="form-builder-empty-description"
                >
                  {t('switch_to_builder_message')}
                </Typography>
                <Button
                  color="primary"
                  onClick={() => setActiveTab('builder')}
                  text={t('go_to_builder')}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Box className="form-builder-action-buttons">
        <Button
          color="primary"
          onClick={handleDiscardModal}
          text={t('Discard')}
          style={{
            padding: '12px 22px',
            width: '157px',
            height: '40px',
          }}
        />

        <Button
          color="primary"
          onClick={saveForm}
          text={t('Save')}
          style={{
            padding: '12px 22px',
            width: '157px',
            height: '40px',
          }}
        />
      </Box>
    </Box>
  );
};

export default FormBuilderComponent;
